import {useLayoutEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {TABLES} from "../../../../Apis/Tables";
import {MODELS} from "../../../../Apis/Models";
import {MATERIAL_CONTROL_TABS, TAB_READ_PERMISSION_MATERIAL_CONTROL} from "../Model/Model";
import {USER_TYPE_NAME_MODEL} from "../../../../enums/userTypeEnum";
import routes from "../../../../Routes/routes";

/**
 * Table model each tab of material control is read with.
 *
 * The requests tab is read with the model of the sub view it is displayed
 * with, so the tab itself does not point the search at a model.
 */
const TAB_TABLE_MODEL = {
    [MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]: TABLES.ENTRY_AND_EXIT_FORMS,
    [MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]: TABLES.VEHICLE_MOVEMENTS
};

/**
 * Module each tab of material control exports its table with.
 *
 * The file is generated by the module the table belongs to, so every tab is
 * exported with the one that lists it and the requests tab, which is read
 * through the sub view it displays, points at none of its own.
 */
const TAB_MODULE_MODEL = {
    [MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]: MODELS.ENTRY_AND_EXIT_FORMS,
    [MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]: MODELS.VEHICLE_MOVEMENTS
};

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
export const MaterialControlMainApp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    let {state} = location;
    const currentTab = state?.currentTab;
    const currentSubView = state?.currentSubView;
    const { permission, user, getUserByIdAtEntry } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const [isAllChecked, setIsAllChecked] = useState(false);
    // Tells the table of the tab to read its first page again, it is raised
    // when the filters of the advanced search are cleared
    const [modified, setModified] = useState(false);
    const { t } = useTranslation();
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState(null);
    // Body of the last advanced search, the export reads it while the filters are applied
    const [criteria, setCriteria] = useState(null);
    // Final array to store filtered data
    const [finalArray, setFinalArray] = useState([]);
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const [searchElement, setSearchElement] = useState('');
    const [selectedList, setSelectedList] = useState([]);
    const [moduleId, setModuleId] = useState(null);
    const [option, setOption] = useState(null);
    const [steps, setSteps] = useState([]);
    // State to track the currently active step index
    const [activeStep, setActiveStep] = useState(null);
    const [subView, setSubView] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(null);
    // A guard carries no permissions, its user type reads the forms and the vehicle
    // movements. The guard at the gate only registers the movements of the vehicles, so the
    // requests of material are not read by it.
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    // The tables list the folios the user takes part in, so the values the advanced
    // search offers are read for that user too. The guard reads every folio, so its
    // values are not scoped to a user.
    const searchUserId = isGuard ? null : getUserByIdAtEntry?.userId;

    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            { tab: MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.TRANS, perm: [TAB_READ_PERMISSION_MATERIAL_CONTROL[MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]], index:MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX },
            { tab: MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.TRANS, perm: [TAB_READ_PERMISSION_MATERIAL_CONTROL[MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]], index:MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX },
            { tab: MATERIAL_CONTROL_TABS.REQUESTS.TRANS, perm: [TAB_READ_PERMISSION_MATERIAL_CONTROL[MATERIAL_CONTROL_TABS.REQUESTS.INDEX]], index:MATERIAL_CONTROL_TABS.REQUESTS.INDEX, hiddenForGuard: true },
        ];

        const auxStep = stepConfig.reduce((acc, { tab, perm, index, hiddenForGuard }) => {
            const hasPermission = DEV || isGuard || perm.some(p => permission?.includes(p));
            if (hasPermission && !(hiddenForGuard && isGuard)) {
                acc.push({label:t(tab), index:index});
                if(activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);

        if(activeTab !== null && !currentTab) handleStepChange(activeTab);
    }, [t, permission, isGuard]);

    useLayoutEffect(() => {
        if(currentTab) handleStepChange(currentTab);
    }, [location]);


    /**
     * Handles changing the active step.
     * Resets the selected items list when the step changes.
     *
     * @param {number} step - The index of the step to activate.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        setSelectedList([]);
        setFinalArray([]);
        setSearchElement('');
        setSearch(false);
        setCriteria(null);
        navigate(location.pathname, { replace: true, state: {} });

        subViewChange(step);
        // The requests tab is pointed at the model of the sub view it is
        // displayed with, so the model the previous tab left pointed is cleared
        setOption(TAB_TABLE_MODEL[step] || null);
        setModuleId(TAB_MODULE_MODEL[step] || null);
    };

    /**
     * Handles the "create" action depending on the current active step.
     * Navigates to the visitor type creation route or shows the invitation modal.
     */
    const subViewChange = (step) => {
        if(currentSubView){
            setSubView(currentSubView);
            return;
        }

        switch (step) {
            case MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX:
                break;
            case MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX:
                break;
            case MATERIAL_CONTROL_TABS.REQUESTS.INDEX:
                break;
        }
    };


    // A guard carries no permissions, so the action tells whether its user type is offered it
    const permissionButtons = (list, guardAccess = false) => {
        const permissionRequired = list[activeStep];

        if (!permissionRequired) {
            return false;
        }
        return DEV || (isGuard ? guardAccess : permission?.includes(permissionRequired));
    };

    /**
     * Handles the "create" action depending on the current active step.
     * The entry and exit forms tab asks for the form to create with a modal,
     * and the vehicle movements tab goes straight to the creation of a movement.
     * The requests tab only reads the folios, so it creates nothing.
     */
    const goCreate = () => {
        switch (activeStep) {
            case MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX:
                setShowCreateModal(true);
                break;
            case MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX:
                navigate(routes.vehicleMovements.create);
                break;
        }
    };


    /**
     * Tells whether the table of a tab is already read with its own model.
     *
     * The table asks for its first page as soon as it is mounted, and a search
     * without a table model is answered with an error by the service, so the
     * table is mounted once the search is pointed at the model of its tab.
     * A tab that comes from the navigation is restored before the model is
     * pointed, so the model is awaited for. The requests tab is pointed by its
     * sub view, which awaits for the model of the sub view on its own.
     *
     * @param {number} step - Tab the table is read with.
     *
     * @returns {boolean} True when the table of the tab can be mounted.
     */
    const isTabReady = (step) => {
        if (activeStep !== step) return false;
        const model = TAB_TABLE_MODEL[step];
        return !model || option === model;
    };

    return {
        t,
        navigate,
        setBodyForExport,
        permission,
        DEV,
        steps,
        setSteps,
        activeStep,
        setActiveStep,
        searchElement,
        setSearchElement,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        modified,
        setModified,
        search,
        moduleId,
        setModuleId,
        option,
        setOption,
        subView,
        setSubView,
        finalArray,
        setFinalArray,
        filterDialogShow,
        setFilterDialogShow,
        bodyForExport,
        criteria,
        setCriteria,
        isAllChecked,
        setIsAllChecked,
        selectedList,
        setSelectedList,
        showCreateModal,
        setShowCreateModal,
        handleStepChange,
        permissionButtons,
        goCreate,
        isTabReady,
        isGuard,
        searchUserId
    }
};
